import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CONTACT_TYPE } from 'app-constants'
import { STATICS } from 'statics'
import { RadioButton } from 'components/radio-button'
import { Input } from 'components/input'
import { ContextLoader } from 'components/context-loader'
import { Button } from 'components/button'
import { formatPhone, formatPhoneMasked, formatPhoneToDB, isPhoneValid } from 'utils'
import { addCustomerContact, handleNewContact } from 'slices/customer'
import { DEFAULT_INVALID_PHONE_ERROR_MESSAGE } from './constants'
import './style.scss'

export function ServiceOrderPhone({ onSelectPhone = () => {} }) {
  const [mainPhone, setMainPhone] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [newPhone, setNewPhone] = useState('')
  const [createNewPhone, setCreateNewPhone] = useState(false)
  const [error, setError] = useState('')
  const {
    info: { contacts },
  } = useSelector(state => state.customer)
  const dispatch = useDispatch()

  useEffect(() => {
    function initMainPhone() {
      if (contact && isPhoneValid(contact)) {
        setMainPhone(formatPhone(contact))
      } else {
        setCreateNewPhone(true)
      }
    }

    const phones = contacts.filter(item => item.contactType === CONTACT_TYPE.PHONE)
    const { contact } = phones.find(phone => phone.principal) || {}

    initMainPhone(contact)
  }, [contacts])

  function handleMainPhoneSelected() {
    if (mainPhone) {
      onSelectPhone(mainPhone)
    } else {
      setError(DEFAULT_INVALID_PHONE_ERROR_MESSAGE)
    }
  }

  async function handleSaveNewContact() {
    const unmaskedPhone = formatPhoneToDB(newPhone)
    const hasPhone = contacts.some(item => item.contact === unmaskedPhone)
    if (hasPhone) return

    const data = {
      contact: unmaskedPhone,
      contactType: CONTACT_TYPE.PHONE,
    }

    try {
      setIsLoading(true)
      await dispatch(addCustomerContact(data)).unwrap()
      dispatch(handleNewContact(data))
    } catch (e) {
      console.error(e)
    } finally {
      setIsLoading(false)
    }
  }

  function handleNewPhoneSelected() {
    if (isPhoneValid(newPhone)) {
      handleSaveNewContact()
      onSelectPhone(formatPhone(newPhone))
    } else {
      setError(DEFAULT_INVALID_PHONE_ERROR_MESSAGE)
    }
  }

  function handleButtonClick() {
    if (createNewPhone) {
      handleNewPhoneSelected()
    } else {
      handleMainPhoneSelected()
    }
  }

  function handlePhoneChange({ target: { value } }) {
    setError('')
    setNewPhone(value)
  }

  function renderInfoCard() {
    return (
      <div className='card-info'>
        <div className='icon-container'>
          <STATICS.EXCLAMATION_MARK className='icon' />
        </div>
        <div className='card-info-content'>
          <div className='title'>Informe um telefone para contato</div>
          <p className='description'>
            Esse número será utilizado pela nossa equipe, caso seja necessário entrar em contato com
            você sobre a visita técnica.
          </p>
        </div>
      </div>
    )
  }

  function renderMainContactOption() {
    if (!mainPhone) return null

    const maskedPhone = formatPhoneMasked(mainPhone)

    return (
      <div className='phone-option-container'>
        <RadioButton
          checked={!createNewPhone}
          label='Contato principal'
          labelClassName='title'
          onChange={() => setCreateNewPhone(false)}
        />
        <div className='main-phone'>{maskedPhone}</div>
      </div>
    )
  }

  function renderNewPhoneOption() {
    return (
      <div className='phone-option-container'>
        <RadioButton
          checked={createNewPhone}
          label='Cadastrar novo telefone'
          labelClassName='title'
          onChange={() => setCreateNewPhone(true)}
        />
        <div className='new-phone'>
          <Input
            className='input'
            name='phone'
            onChange={handlePhoneChange}
            value={newPhone}
            mask='(99) 99999-9999'
            hasError={!!error}
            errorMessage={error}
            placeholder='(__) _____-____'
            onFocus={() => setCreateNewPhone(true)}
          />
        </div>
        <div className='new-phone-info'>
          <STATICS.INFO_OUTLINE className='icon' />
          <p className='description'>
            Esse número será utilizado para contato sobre a sua visita técnica
          </p>
        </div>
      </div>
    )
  }

  function renderPhoneSelection() {
    return (
      <div className='phone-selection'>
        {renderMainContactOption()}
        {renderNewPhoneOption()}
      </div>
    )
  }

  if (isLoading) return <ContextLoader fixed={false} />

  return (
    <div className='service-order-phone'>
      {renderInfoCard()}
      {renderPhoneSelection()}
      <Button className='button' color='primary' size='large' onClick={handleButtonClick}>
        Agendar
      </Button>
    </div>
  )
}
